/* eslint-disable react/jsx-props-no-spreading */
import React, { FC } from 'react';
import { Box, Button, List, Pagination } from '@mui/material';
import { UsePaginationItem } from '@mui/material/usePagination';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import styles from './Pager.module.scss';

interface PagerProps {
    totalPages: number;
    currentPage: number;
    handlePageChange: (event: React.ChangeEvent<unknown> | React.MouseEvent<HTMLButtonElement>, value: number) => void;
}

const Pager: FC<PagerProps> = (props) => {
    const largePageLimit = 1000;
    const { totalPages, currentPage, handlePageChange } = props;

    const getPagingOptions = (): UsePaginationItem[] => {
        const options: UsePaginationItem[] = [];
        options.push({
            onClick: (e) => handlePageChange(e, currentPage - 1),
            type: 'previous',
            disabled: currentPage <= 1,
            page: currentPage - 1,
            selected: false,
        });
        const p = currentPage <= 4 ? 1 : currentPage - 3;
        for (let o = 0; o < 7; o++) {
            const val = o + p;
            options.push({
                onClick: (e) => handlePageChange(e, val),
                type: 'page',
                selected: val === currentPage,
                page: val,
                disabled: false,
            });
        }
        options.push({
            onClick: (e) => handlePageChange(e, currentPage + 1),
            type: 'next',
            disabled: false,
            page: currentPage + 1,
            selected: false,
        });
        return options;
    };

    const pageStyles = ({
        '&.Mui-selected': {
            backgroundColor: '#0000001f',
        },
        '&.Mui-selected:hover': {
            backgroundColor: '#00000014',
        },
        '&:hover': {
            backgroundColor: '#0000000a',
            border: '1px solid #0000003b',
        },
        border: '1px solid #0000003b',
        color: '#000',
        minWidth: '32px',
        px: 1,
        ml: 0.5,
    });

    const extraOptions = totalPages >= largePageLimit ? getPagingOptions() : [];

    return (
        totalPages > 1 ? (
            <div className={styles.Pager} data-testid="Pager">
                {totalPages < largePageLimit ? (
                    <Box sx={{ display: 'flex', justifyContent: 'start', m: 1 }}>
                        <Pagination
                            count={totalPages}
                            siblingCount={3}
                            boundaryCount={1}
                            page={currentPage}
                            onChange={handlePageChange}
                        />
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', justifyContent: 'start', m: 1 }}>
                        <List sx={{ display: 'flex', flexDirection: 'row' }}>
                            {extraOptions.map(({ page, type, selected, ...item }) => {
                                let children = null;
                                switch (type) {
                                    case 'start-ellipsis':
                                    case 'end-ellipsis':
                                        children = '...';
                                        break;
                                    case 'previous':
                                        children = (
                                            <Button
                                                {...item}
                                                variant="outlined"
                                                sx={{ ...pageStyles, height: '36px', width: '32px' }}
                                            >
                                                <NavigateBeforeIcon />
                                            </Button>
                                        );
                                        break;
                                    case 'next':
                                        children = (
                                            <Button
                                                {...item}
                                                variant="outlined"
                                                sx={{ ...pageStyles, height: '36px', width: '32px' }}
                                            >
                                                <NavigateNextIcon />
                                            </Button>
                                        );
                                        break;
                                    case 'page':
                                        children = (
                                            <Button
                                                {...item}
                                                className={selected ? 'Mui-selected' : ''}
                                                variant="outlined"
                                                sx={pageStyles}
                                            >
                                                {page}
                                            </Button>
                                        );
                                        break;
                                    default:
                                        break;
                                }
                                return <li key={`${type}-${page}`}>{children}</li>;
                            })}
                        </List>
                    </Box>
                )}
            </div>
        ) : null
    );
};

export default Pager;
